import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/useSectionNav";
import { EASE } from "./Reveal";

const HEADLINE = ["A space to remember", "who you are."];

const WORDS = ["Move", "Breathe", "Feel", "Connect"];

const RotatingWord = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % WORDS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="inline-flex h-[1.5em] items-center overflow-hidden" data-testid="hero-rotating-word">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={WORDS[idx]}
          className="whitespace-nowrap text-gaia-bg"
          initial={{ y: "115%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-115%", opacity: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
        >
          {WORDS[idx]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const Hero = () => {
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1, 1.12]);
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "5%"]);
  return (
    <section ref={heroRef} id="hero" className="relative flex h-[100svh] w-full flex-col justify-end overflow-hidden bg-gaia-charcoal" data-testid="hero-section">
      <motion.img
        src={IMAGES.hero}
        alt="Looking up into the canopy of an ancient cork oak in Andalucía"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ scale: imgScale, y: imgY }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.8, ease: EASE }}
        loading="eager"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gaia-charcoal/50" />

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-24 md:px-12 md:pb-28">
        <motion.p
          className="mb-8 flex items-center font-sans text-[11px] uppercase tracking-[0.32em] text-gaia-bg/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.3, ease: EASE }}
          data-testid="hero-eyebrow"
        >
          <RotatingWord />
          <motion.span layout="position" className="ml-3">· Southern Spain</motion.span>
        </motion.p>

        <h1 className="max-w-4xl font-serif text-[12.5vw] font-light leading-[1.02] tracking-tight text-gaia-bg md:text-[7vw] lg:text-[5.8vw]" data-testid="hero-headline">
          {HEADLINE.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.1em]">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.2, ease: EASE, delay: 0.4 + i * 0.14 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col items-start gap-9 md:flex-row md:items-end md:justify-between">
          <motion.p
            className="max-w-md font-sans text-base font-light leading-relaxed text-gaia-bg/85"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.2, ease: EASE }}
            data-testid="hero-subheadline"
          >
            Where movement becomes medicine, breath becomes guidance, and stillness becomes home.
          </motion.p>

          <motion.button
            onClick={() => scrollToId("philosophy")}
            data-testid="hero-discover-btn"
            className="group relative overflow-hidden border border-gaia-bg/50 px-10 py-4 font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-bg"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.4, ease: EASE }}
          >
            <span aria-hidden className="absolute inset-0 origin-bottom scale-y-0 bg-gaia-bg transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-y-100" />
            <span className="relative z-10 block transition-colors duration-500 group-hover:text-gaia-charcoal">Discover Gaia Space</span>
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
